export interface SavedPhoto {
  id: string;
  dataUrl: string; // On web: base64 data URL, On iOS/Android: file URI
  thumbnail?: string;
  addedAt: number;
  isDefault?: boolean;
}

export class PhotoManager {
  private static readonly STORAGE_KEY = 'joinsaw_photos';
  private static readonly DEFAULT_IMAGES = [
    '/images/rosie.jpg',
    '/images/scruffy.jpg',
    '/images/scruffy2.jpg',
    '/images/image1.jpg',
    '/images/image2.jpg',
    '/images/image3.jpg',
    '/images/image4.jpg',
    '/images/image5.jpg',
    '/images/image6.jpg',
    '/images/image7.jpg',
    '/images/image8.jpg',
  ];

  static getPhotos(): SavedPhoto[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error loading photos', error);
      return [];
    }
  }

  static getUserPhotos(): SavedPhoto[] {
    return this.getPhotos().filter(p => !p.isDefault);
  }

  static getAllPhotos(): SavedPhoto[] {
    return this.getPhotos();
  }

  static addPhoto(dataUrl: string): SavedPhoto | null {
    const photos = this.getPhotos();

    // Check if photo already exists
    const existing = photos.find(p => p.dataUrl === dataUrl);
    if (existing) return existing;

    const photo: SavedPhoto = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      dataUrl: dataUrl,
      addedAt: Date.now(),
      isDefault: false
    };

    // Add to beginning
    photos.unshift(photo);

    // Try to save, return null if storage is full
    const saved = this.savePhotos(photos);
    if (!saved) {
      return null;
    }
    return photo;
  }

  static deletePhoto(id: string): void {
    const photos = this.getPhotos();
    const filtered = photos.filter(p => p.id !== id);
    this.savePhotos(filtered);
  }

  static getRandomPhoto(): SavedPhoto | null {
    const photos = this.getAllPhotos();
    if (photos.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * photos.length);
    return photos[randomIndex];
  }

  static getDefaultImages(): string[] {
    return [...this.DEFAULT_IMAGES];
  }

  static getRandomImageUrl(): string {
    const userPhotos = this.getUserPhotos();
    const defaultImages = this.getDefaultImages();
    const allImages = [...userPhotos.map(p => p.dataUrl), ...defaultImages];

    // Should always have at least the default images
    const randomIndex = Math.floor(Math.random() * allImages.length);
    return allImages[randomIndex];
  }

  private static savePhotos(photos: SavedPhoto[]): boolean {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(photos));
      return true;
    } catch (error) {
      if (error instanceof DOMException && (
        error.name === 'QuotaExceededError' ||
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      )) {
        console.error('Storage quota exceeded');
        return false;
      }
      console.error('Error saving photos', error);
      return false;
    }
  }
}
